import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { DEAL_STAGES, formatMoney, stageProbability } from "@/lib/crm";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

interface Stats {
  contacts7: number; contacts30: number; openDeals: number;
  pipelineCents: number; weightedCents: number; winRate: number;
  byStage: { stage: string; count: number; value: number }[];
  byDay: { day: string; count: number }[];
}

export default function AdminDashboard() {
  const [s, setS] = useState<Stats | null>(null);

  useEffect(() => {
    (async () => {
      const now = new Date();
      const d7 = new Date(now.getTime() - 7 * 86400e3).toISOString();
      const d30 = new Date(now.getTime() - 30 * 86400e3).toISOString();

      const [{ count: c7 }, { count: c30 }, { data: deals }, { data: recent }] = await Promise.all([
        supabase.from("contacts").select("*", { count: "exact", head: true }).gte("created_at", d7),
        supabase.from("contacts").select("*", { count: "exact", head: true }).gte("created_at", d30),
        supabase.from("deals").select("stage,value_cents,updated_at").is("archived_at", null),
        supabase.from("contacts").select("created_at").gte("created_at", d30),
      ]);

      const all = deals ?? [];
      const open = all.filter(d => d.stage !== "closed_won" && d.stage !== "closed_lost");
      const pipelineCents = open.reduce((a, d) => a + (d.value_cents ?? 0), 0);
      const weightedCents = open.reduce((a, d) => a + (d.value_cents ?? 0) * stageProbability(d.stage), 0);
      const recent30Won = all.filter(d => d.stage === "closed_won" && new Date(d.updated_at) >= new Date(d30));
      const recent30Lost = all.filter(d => d.stage === "closed_lost" && new Date(d.updated_at) >= new Date(d30));
      const winRate = recent30Won.length + recent30Lost.length > 0
        ? recent30Won.length / (recent30Won.length + recent30Lost.length) : 0;

      const byStage = DEAL_STAGES.map(st => {
        const ds = all.filter(d => d.stage === st.id);
        return { stage: st.label, count: ds.length, value: ds.reduce((a, d) => a + (d.value_cents ?? 0), 0) / 100 };
      });

      const dayMap = new Map<string, number>();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400e3).toISOString().slice(0, 10);
        dayMap.set(d, 0);
      }
      (recent ?? []).forEach((r: any) => {
        const k = r.created_at.slice(0, 10);
        if (dayMap.has(k)) dayMap.set(k, (dayMap.get(k) ?? 0) + 1);
      });
      const byDay = Array.from(dayMap.entries()).map(([day, count]) => ({ day: day.slice(5), count }));

      setS({
        contacts7: c7 ?? 0, contacts30: c30 ?? 0, openDeals: open.length,
        pipelineCents, weightedCents, winRate, byStage, byDay,
      });
    })();
  }, []);

  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const tiles = [
    { label: "New contacts (7d)", value: s.contacts7, to: "/admin/contacts" },
    { label: "New contacts (30d)", value: s.contacts30, to: "/admin/contacts" },
    { label: "Open deals", value: s.openDeals, to: "/admin/deals" },
    { label: "Pipeline value", value: formatMoney(s.pipelineCents), to: "/admin/deals" },
    { label: "Weighted pipeline", value: formatMoney(s.weightedCents), to: "/admin/deals" },
    { label: "Win rate (30d)", value: `${Math.round(s.winRate * 100)}%`, to: "/admin/deals" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(t => (
          <Link key={t.label} to={t.to} className="block">
            <Card className="p-4 transition hover:border-primary/50 hover:shadow-md cursor-pointer h-full">
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div className="text-2xl font-semibold text-foreground mt-1">{t.value}</div>
            </Card>
          </Link>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3 text-foreground">New contacts (last 30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={s.byDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3 text-foreground">Deals by stage</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.byStage}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="stage" stroke="hsl(var(--muted-foreground))" fontSize={10} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Legend />
              <Bar dataKey="count" fill="hsl(var(--primary))" name="Deals" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
